import React from 'react';
import { FormikProps, isNaN } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { CustomInputAdornment } from '../../../../form/custom-input-adornment/custom-input-adornment';
import { TextField } from '../../../../form/textfield/textfield';
import { TextFieldWithSlider } from '../../../../form/textfield-with-slider/textfield-with-slider';
import { Grid } from '../../../../grid/grid';
import { Switcher } from '../../../../switcher/switcher';
import { getBigNumberUnit } from '../../../../value/utils/format-big-number';
import { FairPriceFormValues } from '../../../form-values/fair-price-form-values-interface';
import { formatInputWithSpaces, stripSpaces } from '../../../../../../utils/format-with-spaces';
import { cn } from '../../../../../../utils/tailwind';

interface FairPriceTabDcfParametersProps {
    formikProps: FormikProps<FairPriceFormValues>;
    showResult: boolean;
}

const parseRawNumber = (raw: string): number => Number(stripSpaces(raw).replace(',', '.')) || 0;

export const FairPriceTabDcfParameters: React.FC<FairPriceTabDcfParametersProps> = ({ formikProps, showResult }) => {
    const { t } = useTranslation();
    const {
        symbolsStore: { symbolFairPriceStore },
    } = useRootStore();
    const { values, setFieldValue, errors } = formikProps;
    const showError = (field: keyof FairPriceFormValues) => !!errors[field] && !!values[field];

    const fcfUnit = getBigNumberUnit(symbolFairPriceStore.inputDcfTotalFcf ?? 0);
    const cashUnit = getBigNumberUnit(symbolFairPriceStore.inputDcfCash ?? 0);
    const debtUnit = getBigNumberUnit(symbolFairPriceStore.inputDcfDebt ?? 0);
    const sharesUnit = getBigNumberUnit(symbolFairPriceStore.inputDcfSharesOutstanding ?? 0);

    const handleScaledChange = (fieldName: string, raw: string, setStore: (v: number) => void) => {
        const formatted = formatInputWithSpaces(raw);
        setFieldValue(fieldName, formatted);
        setStore(parseRawNumber(formatted) * 1e6);
    };

    return (
        <div
            className={cn(
                'rounded-medium border border-border px-small pt-medium',
                showResult ? 'mb-[-3rem] pb-[calc(theme(spacing.small)+theme(spacing.medium))]' : 'mb-vsmall pb-small',
            )}
        >
            <div className="mb-medium flex items-center justify-between gap-small sm:max:flex-col sm:max:items-start">
                <div className="text-[2.4rem] font-bold leading-[3.2rem] text-brand-secondary">
                    {t('layout.dialogs.fairPrice.dcf.paramsLabel')}
                </div>
                <Switcher
                    value={String(symbolFairPriceStore.dcfYears)}
                    firstOption={{ value: '5', label: '5Y' }}
                    secondOption={{ value: '10', label: '10Y' }}
                    onChange={(value) => symbolFairPriceStore.setDcfYears(Number(value) as 5 | 10)}
                />
            </div>

            <div className="flex flex-col gap-small">
                <Grid cols={{ zero: 1, sm: 3 }} gap={2}>
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.totalFcfInput')}
                        value={values.dcfTotalFcf}
                        fullWidth
                        error={showError('dcfTotalFcf')}
                        helperText={showError('dcfTotalFcf') ? errors.dcfTotalFcf : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment unit={fcfUnit} />,
                        }}
                        onChange={(e) => {
                            handleScaledChange('dcfTotalFcf', e.target.value, (v) => {
                                if (v) symbolFairPriceStore.setInputDcfTotalFcf(v);
                            });
                        }}
                    />
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.growthRate')}
                        value={values.dcfGrowthRate}
                        fullWidth
                        error={showError('dcfGrowthRate')}
                        helperText={showError('dcfGrowthRate') ? errors.dcfGrowthRate : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment suffix="%" />,
                        }}
                        onChange={(e) => {
                            const raw = e.target.value;
                            setFieldValue('dcfGrowthRate', raw);
                            const val = Number(raw.replace(',', '.'));
                            if (!isNaN(val)) symbolFairPriceStore.setInputDcfGrowthRate(val);
                        }}
                    />
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.terminalGrowthRate')}
                        value={values.dcfTerminalGrowthRate}
                        fullWidth
                        error={showError('dcfTerminalGrowthRate')}
                        helperText={showError('dcfTerminalGrowthRate') ? errors.dcfTerminalGrowthRate : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment suffix="%" />,
                        }}
                        onChange={(e) => {
                            const raw = e.target.value;
                            setFieldValue('dcfTerminalGrowthRate', raw);
                            const val = Number(raw.replace(',', '.'));
                            if (!isNaN(val)) symbolFairPriceStore.setInputTerminalGrowthRate(val);
                        }}
                    />
                </Grid>

                <Grid cols={{ zero: 1, sm: 3 }} gap={2}>
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.cash')}
                        value={values.dcfCash}
                        fullWidth
                        error={showError('dcfCash')}
                        helperText={showError('dcfCash') ? errors.dcfCash : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment unit={cashUnit} />,
                        }}
                        onChange={(e) => {
                            handleScaledChange('dcfCash', e.target.value, (v) => {
                                if (v) symbolFairPriceStore.setInputDcfCash(v);
                            });
                        }}
                    />
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.debt')}
                        value={values.dcfDebt}
                        fullWidth
                        error={showError('dcfDebt')}
                        helperText={showError('dcfDebt') ? errors.dcfDebt : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment unit={debtUnit} />,
                        }}
                        onChange={(e) => {
                            handleScaledChange('dcfDebt', e.target.value, (v) => {
                                if (v) symbolFairPriceStore.setInputDcfDebt(v);
                            });
                        }}
                    />
                    <TextField
                        label={t('layout.dialogs.fairPrice.dcf.sharesOutstanding')}
                        value={values.dcfSharesOutstanding}
                        fullWidth
                        error={showError('dcfSharesOutstanding')}
                        helperText={showError('dcfSharesOutstanding') ? errors.dcfSharesOutstanding : undefined}
                        InputProps={{
                            endAdornment: <CustomInputAdornment unit={sharesUnit} />,
                        }}
                        onChange={(e) => {
                            handleScaledChange('dcfSharesOutstanding', e.target.value, (v) => {
                                if (v > 0) symbolFairPriceStore.setInputDcfSharesOutstanding(v);
                            });
                        }}
                    />
                </Grid>

                <Grid cols={{ zero: 1, sm: 2 }} gap={2}>
                    <TextFieldWithSlider
                        sliderValue={Number(values.dcfDiscountRate) || 0}
                        textFieldProps={{
                            label: t('layout.dialogs.fairPrice.dcf.discountRate'),
                            value: values.dcfDiscountRate,
                            fullWidth: true,
                            error: showError('dcfDiscountRate'),
                            helperText: showError('dcfDiscountRate') ? errors.dcfDiscountRate : undefined,
                            InputProps: {
                                endAdornment: <CustomInputAdornment suffix="%" />,
                            },
                            onChange: (e) => {
                                const raw = e.target.value;
                                setFieldValue('dcfDiscountRate', raw);
                                const val = Number(raw.replace(',', '.'));
                                if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputDiscountRate(val);
                            },
                        }}
                        sliderProps={{
                            min: 0,
                            max: 20,
                            step: 0.5,
                            onChange: (_, value) => {
                                const val = value as number;
                                setFieldValue('dcfDiscountRate', String(val));
                                if (val > 0) symbolFairPriceStore.setInputDiscountRate(val);
                            },
                        }}
                    />
                    <TextFieldWithSlider
                        sliderValue={Number(values.dcfMarginOfSafety) || 0}
                        textFieldProps={{
                            label: t('layout.dialogs.fairPrice.dcf.marginOfSafety'),
                            value: values.dcfMarginOfSafety,
                            fullWidth: true,
                            error: showError('dcfMarginOfSafety'),
                            helperText: showError('dcfMarginOfSafety') ? errors.dcfMarginOfSafety : undefined,
                            InputProps: {
                                endAdornment: <CustomInputAdornment suffix="%" />,
                            },
                            onChange: (e) => {
                                const raw = e.target.value;
                                setFieldValue('dcfMarginOfSafety', raw);
                                const val = Number(raw.replace(',', '.'));
                                if (!isNaN(val) && val >= 0 && val < 100)
                                    symbolFairPriceStore.setInputMarginOfSafety(val);
                            },
                        }}
                        sliderProps={{
                            min: 0,
                            max: 50,
                            step: 1,
                            onChange: (_, value) => {
                                const val = value as number;
                                setFieldValue('dcfMarginOfSafety', String(val));
                                if (val >= 0 && val < 100) symbolFairPriceStore.setInputMarginOfSafety(val);
                            },
                        }}
                    />
                </Grid>
            </div>
        </div>
    );
};
